/**
 * 来場者向け 投票フェスモーダル
 * 3 体のキャラから 1 体を選び、詳細を確認して投票するモーダル
 * 回答完了まで閉じない必須導線と、Animated の選択時エフェクトを提供します。
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Animated, Image, Modal, Pressable, ScrollView, Text, View } from '../../../shared/components/WebPrimitives';
import AppButton from '../../../shared/components/AppButton';
import usePublicAppSettings from '../../../shared/hooks/usePublicAppSettings';
import FestivalOrnamentStrip from './FestivalOrnamentStrip';

/** キャラごとのテーマカラー */
const OPTION_THEME = Object.freeze({
  a: { header: '#B91C1C', text: '#FFFFFF', glow: '#FF4444', badge: '#EF4444' },
  b: { header: '#1E3A8A', text: '#FFFFFF', glow: '#3B82F6', badge: '#3B82F6' },
  c: { header: '#14532D', text: '#FFFFFF', glow: '#22C55E', badge: '#22C55E' },
});

/** パーティクルの放射方向（度数） */
const PARTICLE_ANGLES = [0, 40, 80, 120, 160, 200, 240, 280, 320];

/** パーティクルに使う絵文字 */
const PARTICLE_EMOJIS = ['★', '✦', '★', '✦', '★', '✦', '★', '✦', '★'];

/**
 * 来場者向け 投票フェスモーダル
 * @param {Object} props - プロパティ
 * @param {boolean} props.visible - 表示可否
 * @param {string} props.questionText - 質問テキスト
 * @param {Array<{ key: 'a' | 'b' | 'c', label: string, description: string, imageUrl: string }>} props.options - 投票オプション
 * @param {(chosenOption: 'a' | 'b' | 'c') => Promise<void>} props.onSubmit - 投票送信
 * @returns {JSX.Element} 投票モーダル
 */
const VotingFestModal = ({
  visible,
  questionText,
  options,
  onSubmit,
}) => {
  /** 公開アプリ設定 */
  const { settings } = usePublicAppSettings();

  /** 表示ステップ */
  const [step, setStep] = useState('select');
  /** 選択中キー */
  const [selectedKey, setSelectedKey] = useState('');
  /** 送信中フラグ */
  const [isSubmitting, setIsSubmitting] = useState(false);
  /** エラーメッセージ */
  const [errorMessage, setErrorMessage] = useState('');

  /** 選択エフェクト表示中 */
  const [isBurstVisible, setIsBurstVisible] = useState(false);
  /** フラッシュカラー（選択キャラに応じて変化） */
  const [flashColor, setFlashColor] = useState('#FFD700');

  /** フラッシュ不透明度アニメーション */
  const flashOpacity = useRef(new Animated.Value(0)).current;
  /** パーティクル進行度アニメーション（0→1） */
  const burstProgress = useRef(new Animated.Value(0)).current;
  /** 詳細画面の投票確定ボタン */
  const submitButtonRef = useRef<HTMLButtonElement | null>(null);

  /**
   * 詳細画面の投票確定ボタンへfocusする
   * @returns {void}
   */
  const focusSubmitButton = useCallback(() => {
    submitButtonRef.current?.focus();
  }, []);

  /**
   * パーティクル変換データ（burstProgress が安定しているので初回のみ生成）
   */
  const particleData = useMemo(() => (
    PARTICLE_ANGLES.map((deg, i) => {
      const rad = (deg * Math.PI) / 180;
      return {
        key: `prt-${i}`,
        tx: burstProgress.interpolate({ inputRange: [0, 1], outputRange: [0, Math.cos(rad) * 90] }),
        ty: burstProgress.interpolate({ inputRange: [0, 1], outputRange: [0, Math.sin(rad) * 90] }),
        emoji: PARTICLE_EMOJIS[i],
      };
    })
  ), [burstProgress]);

  /** パーティクル全体の不透明度 */
  const particleOpacity = useMemo(
    () => burstProgress.interpolate({ inputRange: [0, 0.15, 0.65, 1], outputRange: [0, 1, 1, 0] }),
    [burstProgress],
  );

  /** パーティクルスケール */
  const particleScale = useMemo(
    () => burstProgress.interpolate({ inputRange: [0, 0.4, 1], outputRange: [0.2, 2.0, 0.5] }),
    [burstProgress],
  );

  /** バーストリングスケール */
  const burstRingScale = useMemo(
    () => burstProgress.interpolate({ inputRange: [0, 1], outputRange: [0.05, 3] }),
    [burstProgress],
  );

  /** バーストリング不透明度 */
  const burstRingOpacity = useMemo(
    () => burstProgress.interpolate({ inputRange: [0, 0.25, 1], outputRange: [1, 0.4, 0] }),
    [burstProgress],
  );

  // 非表示時に状態リセット
  useEffect(() => {
    if (!visible) {
      setStep('select');
      setSelectedKey('');
      setIsSubmitting(false);
      setErrorMessage('');
      setIsBurstVisible(false);
    }
  }, [visible]);

  /**
   * キャラタップ：エフェクト → 詳細画面
   * @param {'a' | 'b' | 'c'} optionKey - 押下キー
   * @returns {void}
   */
  const handlePressOption = useCallback((optionKey) => {
    const theme = OPTION_THEME[optionKey] ?? OPTION_THEME.a;

    setFlashColor(theme.header);
    setIsBurstVisible(true);
    burstProgress.setValue(0);
    flashOpacity.setValue(0);

    Animated.parallel([
      // 画面フラッシュ
      Animated.sequence([
        Animated.timing(flashOpacity, { toValue: 0.6, duration: 55, useNativeDriver: true }),
        Animated.timing(flashOpacity, { toValue: 0, duration: 380, useNativeDriver: true }),
      ]),
      // パーティクル爆発
      Animated.timing(burstProgress, { toValue: 1, duration: 560, useNativeDriver: true }),
    ]).start(() => setIsBurstVisible(false));

    // フラッシュピーク直後に詳細画面へ遷移
    setTimeout(() => {
      setSelectedKey(optionKey);
      setStep('detail');
      setErrorMessage('');
      window.requestAnimationFrame(focusSubmitButton);
    }, 120);
  }, [burstProgress, flashOpacity, focusSubmitButton]);

  /**
   * 詳細画面で「戻る」押下
   * @returns {void}
   */
  const handlePressBack = useCallback(() => {
    setStep('select');
    setErrorMessage('');
  }, []);

  /**
   * 投票送信
   * @returns {Promise<void>}
   */
  const handlePressSubmit = useCallback(async () => {
    if (!selectedKey || isSubmitting) return;
    setIsSubmitting(true);
    setErrorMessage('');
    try {
      await onSubmit(selectedKey);
    } catch (error) {
      setErrorMessage(error?.message ?? '投票の送信に失敗しました。時間をおいて再度お試しください。');
      setIsSubmitting(false);
    }
  }, [isSubmitting, onSubmit, selectedKey]);

  const selectedOption = options.find((o) => o.key === selectedKey) ?? null;
  const selectedTheme = OPTION_THEME[selectedKey] ?? OPTION_THEME.a;

  return (
    <Modal
      accessibilityLabel="企画投票"
      animationType="fade"
      dismissible={false}
      statusBarTranslucent
      trapBrowserBack
      transparent
      visible={visible}
    >
      <View
        style={{
          flex: 1,
          backgroundColor: 'rgba(0,0,0,0.90)',
          alignItems: 'center',
          justifyContent: 'center',
          paddingHorizontal: 8,
          paddingVertical: 12,
        }}
      >
        {/* ── 選択エフェクト群（常に最前面） ── */}

        {/* 全画面カラーフラッシュ */}
        <Animated.View
          pointerEvents="none"
          style={{
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: flashColor,
            opacity: flashOpacity,
            zIndex: 200,
          }}
        />

        {/* バーストリング */}
        {isBurstVisible && (
          <Animated.View
            pointerEvents="none"
            style={{
              position: 'absolute',
              width: 140, height: 140,
              borderRadius: 70,
              borderWidth: 6,
              borderColor: flashColor,
              zIndex: 201,
              transform: [{ scale: burstRingScale }],
              opacity: burstRingOpacity,
            }}
          />
        )}

        {/* 放射パーティクル */}
        {isBurstVisible && particleData.map((p) => (
          <Animated.View
            key={p.key}
            pointerEvents="none"
            style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              marginLeft: -12,
              marginTop: -14,
              zIndex: 202,
              opacity: particleOpacity,
              transform: [
                { translateX: p.tx },
                { translateY: p.ty },
                { scale: particleScale },
              ],
            }}
          >
            <Text style={{ color: '#FFD700', fontSize: 22, fontWeight: '900' }}>{p.emoji}</Text>
          </Animated.View>
        ))}

        {/* ── 背景装飾（コーナースター） ── */}
        {[
          { top: 16, left: 10 }, { top: 16, right: 10 },
          { bottom: 20, left: 10 }, { bottom: 20, right: 10 },
        ].map((pos, i) => (
          <View key={`deco-${i}`} pointerEvents="none" style={{ position: 'absolute', ...pos }}>
            <Text style={{ color: '#FFD700', fontSize: 20, opacity: 0.65 }}>{i % 2 === 0 ? '★' : '✦'}</Text>
          </View>
        ))}

        {/* ── メインパネル ── */}
        <View
          testID="voting-fest-dialog-panel"
          style={{
            display: 'flex',
            flexDirection: 'column',
            maxHeight: 'calc(100dvh - 24px)',
            minHeight: 0,
            width: '100%',
            maxWidth: 480,
            borderWidth: 4,
            borderColor: '#1A2D4D',
            overflow: 'hidden',
            shadowColor: '#FFD700',
            shadowOffset: { width: 0, height: 0 },
            shadowOpacity: 0.85,
            shadowRadius: 22,
            elevation: 28,
          }}
        >
          {/* 上部装飾帯 */}
          <FestivalOrnamentStrip />

          {/* フェスヘッダー */}
          <View
            style={{
              backgroundColor: '#130B2E',
              borderBottomWidth: 3,
              borderBottomColor: '#1A2D4D',
              flexShrink: 0,
              paddingHorizontal: 12,
              paddingTop: 10,
              paddingBottom: 8,
            }}
          >
            {/* フェス表記 */}
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
              <View style={{ flex: 1, height: 1, backgroundColor: '#FFD700', opacity: 0.45 }} />
              <Text style={{ color: '#FFD700', fontSize: 10, fontWeight: '900', letterSpacing: 2.5 }}>
                🎪 {settings.festivalLabelEn} 🎪
              </Text>
              <View style={{ flex: 1, height: 1, backgroundColor: '#FFD700', opacity: 0.45 }} />
            </View>

            {/* 質問文バナー（金地） */}
            <View
              style={{
                marginTop: 8,
                backgroundColor: '#FFD700',
                borderWidth: 2,
                borderColor: '#1A2D4D',
                paddingVertical: 7,
                paddingHorizontal: 10,
              }}
            >
              <Text
                style={{ color: '#130B2E', fontSize: 15, fontWeight: '900', textAlign: 'center', letterSpacing: 0.3 }}
                numberOfLines={2}
              >
                {questionText || `${settings.festivalName}のマスコットキャラクターを選ぼう！`}
              </Text>
            </View>

            {/* バッジ列 */}
            <View style={{ marginTop: 7, flexDirection: 'row', justifyContent: 'center', gap: 6 }}>
              {[
                { label: '🎉 期間限定', bg: '#9B1C1C' },
                { label: '🗳 一人一票', bg: '#1E3A8A' },
                { label: '✓ 回答必須', bg: '#14532D' },
              ].map((badge) => (
                <View key={badge.label} style={{ backgroundColor: badge.bg, paddingHorizontal: 8, paddingVertical: 3, borderWidth: 1, borderColor: '#FFD700' }}>
                  <Text style={{ color: '#FFD700', fontSize: 9, fontWeight: '900', letterSpacing: 0.5 }}>{badge.label}</Text>
                </View>
              ))}
            </View>
          </View>

          {/* ── コンテンツエリア ── */}
          <View style={{ backgroundColor: '#FFFCE8', flex: 1, minHeight: 0 }}>
            {step === 'select' ? (
              // ── キャラ選択 ──
              <ScrollView
                contentContainerStyle={{ padding: 10 }}
                style={{ flex: 1, minHeight: 0 }}
                testID="voting-fest-dialog-content"
              >
                {/* ガイドテキスト */}
                <View
                  style={{
                    backgroundColor: '#FFF4C2',
                    borderWidth: 2,
                    borderColor: '#1A2D4D',
                    borderStyle: 'dashed',
                    paddingVertical: 6,
                    paddingHorizontal: 10,
                    marginBottom: 10,
                  }}
                >
                  <Text style={{ color: '#1A2D4D', fontSize: 12, fontWeight: '900', textAlign: 'center' }}>
                    ★ 3体から1体を選んで投票してください ★
                  </Text>
                  <Text style={{ color: '#344766', fontSize: 10, textAlign: 'center', marginTop: 2 }}>
                    投票が完了するとホームへ進めます
                  </Text>
                </View>

                {/* 3列キャラグリッド */}
                <View style={{ flexDirection: 'row', gap: 7 }}>
                  {options.map((option) => {
                    const theme = OPTION_THEME[option.key] ?? OPTION_THEME.a;
                    const label = option.label || `キャラ${option.key.toUpperCase()}`;

                    return (
                      <Pressable
                        accessibilityLabel={`${label}を選ぶ`}
                        accessibilityRole="button"
                        className="active:scale-[0.94]"
                        key={option.key}
                        onPress={() => handlePressOption(option.key)}
                        style={{
                          flex: 1,
                          minHeight: 44,
                          borderWidth: 3,
                          borderColor: '#1A2D4D',
                          overflow: 'hidden',
                          shadowColor: theme.glow,
                          shadowOffset: { width: 0, height: 2 },
                          shadowOpacity: 0.55,
                          shadowRadius: 8,
                          elevation: 7,
                        }}
                      >
                        {/* カードヘッダー */}
                        <View
                          style={{
                            backgroundColor: theme.header,
                            paddingVertical: 5,
                            borderBottomWidth: 2,
                            borderBottomColor: '#1A2D4D',
                          }}
                        >
                          <Text
                            style={{
                              color: '#FFD700',
                              fontSize: 9,
                              fontWeight: '900',
                              textAlign: 'center',
                              letterSpacing: 1,
                            }}
                          >
                            候補 {option.key.toUpperCase()}
                          </Text>
                        </View>

                        {/* キャラ画像 */}
                        <View
                          style={{
                            backgroundColor: '#FFF8DD',
                            alignItems: 'center',
                            justifyContent: 'center',
                            paddingVertical: 8,
                            borderBottomWidth: 1,
                            borderBottomColor: '#E0D0A0',
                          }}
                        >
                          {option.imageUrl ? (
                            <Image
                              accessibilityLabel={`${label}の画像`}
                              decoding="async"
                              height={82}
                              loading="lazy"
                              resizeMode="contain"
                              source={{ uri: option.imageUrl }}
                              style={{ width: 82, height: 82 }}
                              width={82}
                            />
                          ) : (
                            <View style={{ width: 82, height: 82, alignItems: 'center', justifyContent: 'center' }}>
                              <Text style={{ fontSize: 38 }}>🎭</Text>
                            </View>
                          )}
                        </View>

                        {/* キャラ名 */}
                        <View style={{ backgroundColor: '#FFFDF0', padding: 5, minHeight: 44, alignItems: 'center', justifyContent: 'center' }}>
                          <Text
                            style={{ color: '#1A2D4D', fontSize: 11, fontWeight: '900', textAlign: 'center', lineHeight: 16 }}
                            numberOfLines={2}
                          >
                            {label}
                          </Text>
                        </View>

                        {/* 投票 CTA */}
                        <View
                          style={{
                            backgroundColor: theme.header,
                            paddingVertical: 6,
                            alignItems: 'center',
                            borderTopWidth: 2,
                            borderTopColor: '#1A2D4D',
                          }}
                        >
                          <Text style={{ color: '#FFD700', fontSize: 11, fontWeight: '900', letterSpacing: 0.5 }}>
                            投票！★
                          </Text>
                        </View>
                      </Pressable>
                    );
                  })}
                </View>

                {/* 注記 */}
                <Text style={{ color: '#344766', fontSize: 9, textAlign: 'center', marginTop: 8 }}>
                  ※ タップ後に詳細確認があります。投票は一回限りです。
                </Text>
              </ScrollView>
            ) : (
              // ── 詳細・投票確認 ──
              <ScrollView
                contentContainerStyle={{ padding: 10, gap: 10 }}
                style={{ flex: 1, minHeight: 0 }}
                testID="voting-fest-dialog-content"
              >
                {/* キャラ名バナー */}
                <View
                  style={{
                    backgroundColor: selectedTheme.header,
                    borderWidth: 2,
                    borderColor: '#1A2D4D',
                    paddingVertical: 10,
                    paddingHorizontal: 16,
                    alignItems: 'center',
                  }}
                >
                  <Text style={{ color: '#FFD700', fontSize: 10, fontWeight: '900', letterSpacing: 2 }}>
                    CANDIDATE {(selectedKey || 'A').toUpperCase()}
                  </Text>
                  <Text
                    style={{ color: '#FFFFFF', fontSize: 22, fontWeight: '900', textAlign: 'center', marginTop: 2 }}
                    numberOfLines={2}
                  >
                    {selectedOption?.label || `キャラクター${(selectedKey || 'A').toUpperCase()}`}
                  </Text>
                </View>

                {/* キャラ画像 大 */}
                <View style={{ alignItems: 'center' }}>
                  <View
                    style={{
                      borderWidth: 4,
                      borderColor: selectedTheme.header,
                      backgroundColor: '#FFF8DD',
                      padding: 6,
                      shadowColor: selectedTheme.glow,
                      shadowOffset: { width: 0, height: 0 },
                      shadowOpacity: 0.85,
                      shadowRadius: 18,
                      elevation: 14,
                    }}
                  >
                    {selectedOption?.imageUrl ? (
                      <Image
                        accessibilityLabel={`${selectedOption.label || ''}の画像`}
                        decoding="async"
                        height={170}
                        resizeMode="contain"
                        source={{ uri: selectedOption.imageUrl }}
                        style={{ width: 170, height: 170 }}
                        width={170}
                      />
                    ) : (
                      <View style={{ width: 170, height: 170, alignItems: 'center', justifyContent: 'center', backgroundColor: '#FFF0D0' }}>
                        <Text style={{ fontSize: 72 }}>🎭</Text>
                      </View>
                    )}
                  </View>
                </View>

                {/* キャラ紹介 */}
                <View style={{ backgroundColor: '#FFFDF0', borderWidth: 2, borderColor: '#1A2D4D', padding: 12 }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                    <View style={{ width: 4, height: 14, backgroundColor: selectedTheme.header }} />
                    <Text style={{ color: '#1A2D4D', fontSize: 12, fontWeight: '900' }}>キャラクター紹介</Text>
                  </View>
                  <Text style={{ color: '#182843', fontSize: 13, lineHeight: 22 }}>
                    {selectedOption?.description || '（説明はまだ登録されていません）'}
                  </Text>
                </View>

                {/* 確認ボックス */}
                <View
                  style={{
                    backgroundColor: '#FFF4C2',
                    borderWidth: 3,
                    borderColor: '#FFD700',
                    paddingVertical: 10,
                    paddingHorizontal: 12,
                  }}
                >
                  <Text style={{ color: '#1A2D4D', fontSize: 14, fontWeight: '900', textAlign: 'center' }}>
                    ★ このキャラクターに投票しますか？ ★
                  </Text>
                  <Text style={{ color: '#344766', fontSize: 10, textAlign: 'center', marginTop: 3 }}>
                    投票は一回限りです。取り消しできません。
                  </Text>
                </View>

                {errorMessage ? (
                  <Text style={{ color: '#C23737', fontSize: 12, fontWeight: '700', textAlign: 'center' }}>
                    {errorMessage}
                  </Text>
                ) : null}

              </ScrollView>
            )}
          </View>

          <View
            testID="voting-fest-dialog-actions"
            style={{
              backgroundColor: '#FFFCE8',
              borderTopColor: '#1A2D4D',
              borderTopWidth: 2,
              flexShrink: 0,
              paddingHorizontal: 10,
              paddingVertical: 8,
            }}
          >
            {step === 'detail' ? (
              <>
                <Pressable
                  accessibilityRole="button"
                  className="active:scale-[0.97]"
                  disabled={isSubmitting}
                  onPress={handlePressSubmit}
                  ref={submitButtonRef}
                  style={{
                    alignItems: 'center',
                    backgroundColor: isSubmitting ? '#888' : selectedTheme.header,
                    borderColor: '#1A2D4D',
                    borderWidth: 3,
                    justifyContent: 'center',
                    minHeight: 48,
                    paddingHorizontal: 8,
                    paddingVertical: 8,
                    shadowColor: selectedTheme.glow,
                    shadowOffset: { width: 0, height: 0 },
                    shadowOpacity: 0.5,
                    shadowRadius: 7,
                    elevation: 6,
                  }}
                >
                  <Text style={{ color: '#FFFFFF', fontSize: 16, fontWeight: '900', letterSpacing: 0.5, textAlign: 'center' }}>
                    {isSubmitting ? '投票中...' : '✦ このキャラクターに投票する！ ✦'}
                  </Text>
                </Pressable>

                <View style={{ marginTop: 8 }}>
                  <AppButton
                    disabled={isSubmitting}
                    label="← 戻って選び直す"
                    onPress={handlePressBack}
                    variant="ghost"
                  />
                </View>
              </>
            ) : (
              <Text
                style={{
                  color: '#1A2D4D',
                  fontSize: 11,
                  fontWeight: '900',
                  lineHeight: 17,
                  textAlign: 'center',
                }}
              >
                候補をタップして、詳細を確認してください
              </Text>
            )}
            <Text
              style={{
                color: '#344766',
                fontSize: 9,
                marginTop: 4,
                textAlign: 'center',
              }}
            >
              投票すると次へ進めます。この画面を閉じずに投票してください。
            </Text>
          </View>

          {/* 下部装飾帯 */}
          <FestivalOrnamentStrip />
        </View>
      </View>
    </Modal>
  );
};

export default VotingFestModal;
